import { defineStore } from 'pinia';
import { useToast } from '../composables/useToast';
import { ref } from 'vue';
import { exchangeOAuth2Code, getWxworkConfig, type WxworkPublicConfig } from '../api/internal';
import { OAUTH_CALLBACK_PATH, safeReturnPath } from '../api/http';

const STORAGE_KEY = 'cat.internal.auth';
const CORPID_CACHE_KEY = 'cat.corpid';
const AGENTID_CACHE_KEY = 'cat.agentid';

interface AuthState {
  token: string;
  expiresAt: string;
  userId: string;
  username: string;
  wxworkUserId: string;
}

/**
 * 同步从 localStorage 读取已保存的认证状态。
 * 必须同步 — router guard 在页面 hard-load 后立刻评估当前路由,如果 store 用 async
 * bootstrap 才填 token,guard 跑得比 bootstrap 快,会误判为"未登录" → 跳 OAuth2 → 死循环。
 *
 * 不在客户端做 expiresAt 检查 — 后端 .NET DateTime 序列化在某些路径下不带 Z 后缀,
 * 浏览器按 local time 解析会把 UTC 时间错位 8 小时,刚拿到的 token 就被误判过期。
 * 真过期由 http.ts 的 401 拦截兜底处理,可靠且简单。
 */
function readInitialState(): AuthState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as AuthState;
    if (!state.token) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return state;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = readInitialState();
  const token = ref<string>(initial?.token ?? '');
  const username = ref<string>(initial?.username ?? '');
  const userId = ref<string>(initial?.userId ?? '');
  const wxworkUserId = ref<string>(initial?.wxworkUserId ?? '');

  /**
   * 启动后异步刷新一次状态(过期清理等)。同步初始化已经在 store 构造时完成,
   * bootstrap 保留主要为兼容老调用路径,可重复调用。
   */
  async function bootstrap() {
    const state = readInitialState();
    if (state) {
      token.value = state.token;
      username.value = state.username;
      userId.value = state.userId;
      wxworkUserId.value = state.wxworkUserId;
    } else {
      token.value = '';
      username.value = '';
      userId.value = '';
      wxworkUserId.value = '';
    }
  }

  /**
   * 跳转到企业微信 OAuth2 页让员工授权。
   * corpid 来源(按优先级):
   *   1. URL ?corpid=...(企微在某些场景会自动带,比如自定义菜单链接)
   *   2. sessionStorage 缓存(同标签页内复用)
   *   3. 调 GET /api/internal/wxwork-config 让后端返(主路径,
   *      因为企微工作台默认不会自动加 ?corpid)
   * returnTo 只接受站内且非回调页的路径, 否则回首页。
   */
  async function beginAuthorize(returnTo: string) {
    const cfg = await resolveWxworkConfigAsync();
    if (!cfg.corpId) {
      useToast().error('企业微信登录配置缺失 (企业 ID), 请联系管理员');
      return;
    }
    if (!cfg.agentId) {
      useToast().error('企业微信登录配置缺失 (应用 ID), 请联系管理员');
      return;
    }
    const redirect = `${window.location.origin}${OAUTH_CALLBACK_PATH}?return=${encodeURIComponent(safeReturnPath(returnTo))}`;
    // scope=snsapi_privateinfo 走"敏感信息授权"流, 后端 ExchangeOAuth2CodeAsync 会拿到 user_ticket
    // 再调 auth/getuserdetail 拿员工头像落库. 企微管理后台需在「应用详情→敏感信息授权」勾选「头像」.
    // snsapi_base 已无法拿到 avatar (通讯录同步 user/get 自 2022-08-15 起禁用新 IP).
    // agentid 在 snsapi_privateinfo 时必填, 否则企微返「无效的 agentid 参数」.
    const url = `https://open.weixin.qq.com/connect/oauth2/authorize`
      + `?appid=${encodeURIComponent(cfg.corpId)}`
      + `&redirect_uri=${encodeURIComponent(redirect)}`
      + `&response_type=code`
      + `&scope=snsapi_privateinfo`
      + `&agentid=${encodeURIComponent(cfg.agentId)}`
      + `&state=cat_internal`
      + `#wechat_redirect`;
    window.location.replace(url);
  }

  /**
   * OAuth2 回调:用 code 换 Cat 内部 JWT。
   */
  async function completeOAuth2(code: string) {
    const corpId = await resolveCorpIdAsync();
    if (!corpId) throw new Error('缺少 corpid');
    const result = await exchangeOAuth2Code(code, corpId);
    const state: AuthState = {
      token: result.token,
      expiresAt: result.expiresAt,
      userId: result.userId,
      username: result.username,
      wxworkUserId: result.wxworkUserId,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    token.value = state.token;
    username.value = state.username;
    userId.value = state.userId;
    wxworkUserId.value = state.wxworkUserId;
  }

  function logout() {
    localStorage.removeItem(STORAGE_KEY);
    token.value = '';
    username.value = '';
    userId.value = '';
    wxworkUserId.value = '';
  }

  /**
   * 三级 fallback 解析 corpid:URL → sessionStorage → 后端 config 端点。
   * 缓存到 sessionStorage 避免每次跳转都打后端。
   */
  async function resolveCorpIdAsync(): Promise<string> {
    return (await resolveWxworkConfigAsync()).corpId;
  }

  /**
   * 解析 corpid + agentid (snsapi_privateinfo 必须两个都有). 同 corpid 三级 fallback,
   * agentid 只能从后端 config 拿 — 企微工作台 URL 不会自动带 agentid.
   */
  async function resolveWxworkConfigAsync(): Promise<{ corpId: string; agentId: string }> {
    const fromQuery = new URLSearchParams(window.location.search).get('corpid');
    if (fromQuery) sessionStorage.setItem(CORPID_CACHE_KEY, fromQuery);

    const cachedCorp = fromQuery || sessionStorage.getItem(CORPID_CACHE_KEY) || '';
    const cachedAgent = sessionStorage.getItem(AGENTID_CACHE_KEY) || '';
    if (cachedCorp && cachedAgent) return { corpId: cachedCorp, agentId: cachedAgent };

    try {
      const cfg: WxworkPublicConfig = await getWxworkConfig();
      if (cfg.corpId) sessionStorage.setItem(CORPID_CACHE_KEY, cfg.corpId);
      if (cfg.agentId) sessionStorage.setItem(AGENTID_CACHE_KEY, cfg.agentId);
      return { corpId: cfg.corpId || cachedCorp, agentId: cfg.agentId || cachedAgent };
    } catch (e) {
      console.warn('获取 wxwork-config 失败', e);
      return { corpId: cachedCorp, agentId: cachedAgent };
    }
  }

  return {
    token,
    username,
    userId,
    wxworkUserId,
    bootstrap,
    beginAuthorize,
    completeOAuth2,
    logout,
  };
});
